import { useEffect, useRef } from 'react';
import { GoogleMap } from '@capacitor/google-maps';
import type { Circle } from '@capacitor/google-maps';
import {
  BASE_MAP_CONFIG,
  dropoffRadiusCircle,
  getBaseMapLocale,
  zoomToFitRadius,
} from '../../utils/mapConfig';

type Props = {
  /** Center of the map. When showing obfuscated location this is the parsed obfuscated coords. */
  center: { lat: number; lng: number };
  zoom?: number;
  /** When true, renders a 200 m translucent circle instead of a pin. */
  showCircle?: boolean;
  /** When true, re-enables the Street View pegman on top of the stripped-down default UI. */
  enableStreetView?: boolean;
  /**
   * Drop-off radius in miles to draw around the pin, zoomed so the whole
   * circle fits. Owner-only (the chef previewing the area they drive); pass a
   * precise `center` with it, never the obfuscated one.
   */
  radiusMiles?: number | null;
};

export const ListingLocationMap: React.FC<Props> = ({
  center,
  zoom = 14,
  showCircle = false,
  enableStreetView = false,
  radiusMiles = null,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<GoogleMap | null>(null);

  useEffect(() => {
    let cancelled = false;
    let createdMap: GoogleMap | null = null;

    // NOTE(maps): React 19 StrictMode double-invokes effects in dev (mount →
    // unmount → mount). @capacitor/google-maps has a race where destroy()
    // removes the map from its internal registry but Google Maps' queued
    // event callbacks still fire afterward and crash in getMapBounds. We
    // defer the create through a 0ms timeout so the StrictMode cleanup
    // cancels the first attempt before it ever calls GoogleMap.create.
    const timeoutId = window.setTimeout(() => {
      if (cancelled) return;
      void create();
    }, 0);

    const create = async () => {
      if (!mapRef.current) return;
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        console.warn('[ListingLocationMap] VITE_GOOGLE_MAPS_API_KEY is not set');
        return;
      }

      const map = await GoogleMap.create({
        id: `listing-location-map-${Date.now()}`,
        element: mapRef.current,
        apiKey,
        ...getBaseMapLocale(),
        config: {
          ...BASE_MAP_CONFIG,
          center,
          zoom:
            radiusMiles && mapRef.current
              ? zoomToFitRadius(
                  center.lat,
                  radiusMiles,
                  mapRef.current.clientWidth,
                  mapRef.current.clientHeight,
                )
              : zoom,
          streetViewControl: enableStreetView,
        },
      });

      if (cancelled) {
        await map.destroy();
        return;
      }
      createdMap = map;
      instanceRef.current = map;

      if (showCircle) {
        // When zoomed out the 200 m circle shrinks to a hard-to-spot dot, so
        // below a zoom threshold we swap it for a marker pin that stays crisp,
        // then swap back to the circle when zoomed back in. A one-level dead
        // zone (pin < 12, circle >= 13) keeps it from flip-flopping when the
        // camera parks right at the boundary. Driven by onCameraIdle, which
        // fires once the gesture settles (no mid-pinch flicker).
        const PIN_BELOW = 13;
        const CIRCLE_AT = 14;
        let mode: 'circle' | 'pin' = zoom >= CIRCLE_AT ? 'circle' : 'pin';
        let circleIds: string[] = [];
        let markerId: string | null = null;
        let swapping = false;

        const showCircleOverlay = async () => {
          const circle: Circle = {
            center,
            radius: 200,
            strokeColor: '#2563eb',
            strokeOpacity: 0.6,
            strokeWeight: 2,
            fillColor: '#60a5fa',
            fillOpacity: 0.16,
          };
          circleIds = await map.addCircles([circle]);
        };
        const removeCircleOverlay = async () => {
          if (circleIds.length) {
            await map.removeCircles(circleIds);
            circleIds = [];
          }
        };
        const showPin = async () => {
          markerId = await map.addMarker({ coordinate: center });
        };
        const removePin = async () => {
          if (markerId) {
            await map.removeMarker(markerId);
            markerId = null;
          }
        };

        if (mode === 'circle') {
          await showCircleOverlay();
        } else {
          await showPin();
        }

        await map.setOnCameraIdleListener(async ({ zoom: z }) => {
          if (cancelled || swapping) return;
          const next: 'circle' | 'pin' =
            z < PIN_BELOW ? 'pin' : z >= CIRCLE_AT ? 'circle' : mode;
          if (next === mode) return;
          swapping = true;
          try {
            if (next === 'pin') {
              await removeCircleOverlay();
              await showPin();
            } else {
              await removePin();
              await showCircleOverlay();
            }
            mode = next;
          } finally {
            swapping = false;
          }
        });
      } else {
        await map.addMarker({ coordinate: center });
        if (radiusMiles) {
          await map.addCircles([dropoffRadiusCircle(center, radiusMiles)]);
        }
      }
    };

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      if (createdMap) {
        createdMap.destroy();
        createdMap = null;
      }
      instanceRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={mapRef}
      className="h-64 w-full rounded-lg overflow-hidden bg-neutral-200"
      style={{ touchAction: 'none' }}
    />
  );
};
